/* eslint-disable no-underscore-dangle */
// Types for Model Reviewer/Preparer Dashboard using Fulfillment API

import { Fulfillment } from 'api/models/Fulfillment';
import { AttioSortDirection, SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';
import { FilterTypeSelection, ModelFilterType } from 'dashboard409a/components/ModelsTable/ModelsTable.types';

export interface Note {
  id: number;
  content: string;
  author_id: number;
  author_email: string;
  author_name: string;
  created_at: string;
  updated_at: string;
}

export interface FulfillmentWithCompanyName extends Fulfillment {
  company_name: string;
  company_id: number;
  company_slug: string;
  attio_valuation_id: string;
  attio_company_id?: string;
  note?: Note | null;
  measurement_date?: string;
  measurement_date_slug?: string;
}

export interface ModelWithValuationData extends FulfillmentWithCompanyName {
  valuation_status?: string;
  engagement_type?: string;
  valuation_date?: string;
  valuation_lead?: string;
  valuation_lead_image_src?: string;
  is_loading_valuation_data?: boolean;
  notes?: string; // Keep for backward compatibility
  notes_count?: number; // Total analyst notes across all users for this fulfillment
}

// User roles for filtering
export enum UserRole {
  REVIEWER = 'Reviewer',
  PREPARER = 'Preparer',
}

// Helper function to get status string from enum
export const getModelStatusString = (status: Fulfillment.model_status): string => {
  const statusMap = {
    [Fulfillment.model_status._0]: 'Draft',
    [Fulfillment.model_status._1]: 'Review',
    [Fulfillment.model_status._2]: 'In-process ScalarUSA',
    [Fulfillment.model_status._3]: 'Final',
    [Fulfillment.model_status._4]: 'Review Final',
    [Fulfillment.model_status._5]: 'COMPLETE',
  };
  return statusMap[status] || 'Unknown';
};

// eslint-disable-next-line no-underscore-dangle
export const isCompletedStatus = (status: Fulfillment.model_status): boolean => status === Fulfillment.model_status._5;

export interface UseModelsWithValuationDataProps {
  filterType: ModelFilterType;
  filterTypeSelection?: FilterTypeSelection;
  searchStatus?: string[];
  preparerUserIds?: number[];
  reviewerUserIds?: number[];
  disableSorting?: boolean;
  orderBy?: string;
  order?: AttioSortDirection;
  sortCriteria?: SortCriteria[];
  fromDate?: string;
  toDate?: string;
  companyName?: string;
}

export interface UseModelsWithValuationDataReturn {
  rows: ModelWithValuationData[];
  totalCount: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  error: unknown;
  updateModelNote: (modelId: number, noteContent: string, noteId?: number) => void;
  updateModelStatus: (modelId: number, newStatus: number) => void;
  updateModelDueDate: (modelId: number, newDueDate: string) => void;
  updateModelValuationFields: (attioValuationId: string, updates: { valuation_status?: string }) => void;
  refetch: () => void;
}
